import {inject, injectable} from "inversify";
import {BankCategory} from "@/modules/finances/enums";
import {EventEmitter} from "@/modules/base/events";
import {Season} from "@/modules/season/entity";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {Terrace} from "@/modules/stadium/entity";
import {BeforeTerraceExpendedEvent} from "@/modules/stadium/events/BeforeTerraceExpendedEvent";
import {AfterTerraceExpendedEvent} from "@/modules/stadium/events/AfterTerraceExpendedEvent";
import {GateTypeEnum} from "@/modules/action-points/enums";
import GateManager from "@/modules/action-points/services/GateManager";

@injectable()
export default class TerraceExpander {
  constructor(
    @inject(GateManager) private gateManager: GateManager,
    @inject(EventEmitter) private em: EventEmitter,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
  ) {
  }

  public async finish(terrace: Terrace) {
    if (!terrace.seatsTarget || !terrace.seatsFinishedAt) {
      throw 'There is not expansion planed for this terrace.';
    }

    const season = await Season.current();

    if (terrace.seatsFinishedAt > season.currentDateRaw) {
      throw 'Expansion is not finished.';
    }

    await this.em.emitAsync(new BeforeTerraceExpendedEvent(terrace, terrace.seats, terrace.seatsTarget));

    terrace.seats = terrace.seatsTarget;
    terrace.seatsTarget = null;
    terrace.seatsFinishedAt = null;
    await terrace.save();

    await this.em.emitAsync(new AfterTerraceExpendedEvent(terrace));
  }

  public async queue(terrace: Terrace, newSeats: number) {
    if (terrace.seatsFinishedAt) {
      throw 'Terrace is expansion is ongoing.';
    }

    if (!terrace.available) {
      throw 'Terrace is not available.';
    }

    const season = await Season.current();
    const duration = this.configRepository.get('stadium.durations.terrace')(terrace, newSeats);
    const price = this.configRepository.get('stadium.costs.terrace')(terrace, newSeats);

    await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.subtract(
      price,
      {
        category: BankCategory.STADIUM,
        label: translator.t('stadium_terrace_expansion'),
      }
    );

    terrace.seatsTarget = newSeats;
    terrace.seatsFinishedAt = season.currentDate.add(duration, 'days').toDate();
    await terrace.save();
  }
}
